import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  Building2,
  ChevronRight,
  Clock3,
  CreditCard,
  FileText,
  Loader2,
  Package,
  ReceiptText,
  Settings,
  ShoppingCart,
  UserRound,
  UsersRound,
  X
} from 'lucide-react';
import { PlanType } from 'kmstack-shared/core';
import { useActiveCurrency, useKmstackQuery, useSessionContext } from 'kmstack-shared/hooks';
import { formatMoney } from 'kmstack-shared/apps/common';
import { useNavigate } from 'react-router-dom';

import { SearchIcon } from '../assets/icons';
import { useApi } from '../common/hooks/useApi';
import { featureFlagsForLink } from '../common/utils/tabs';
import { cn } from '../common/ui/Core';
import { hasAnyFeature, type AccountAdminApi } from 'kmstack-shared/apps/accounting';
import { Bill, DashboardViewTabs, EReportCategory, Employee, ExpenseTransaction, ExtendedKmstackABParty, Invoice, NavLink, Product, Service } from '../types';

type GlobalSearchCategory =
  | 'All'
  | 'Dashboard'
  | 'Invoices'
  | 'Bills'
  | 'Clients'
  | 'Suppliers'
  | 'Employees'
  | 'Workforce'
  | 'Reports'
  | 'Payments'
  | 'Purchases'
  | 'Inventory'
  | 'Automation'
  | 'Settings'
  | 'Integrations'
  | 'Security';

type GlobalSearchSource = 'record' | 'page' | 'command';

type GlobalSearchResult = {
  id: string;
  title: string;
  href: string;
  category: Exclude<GlobalSearchCategory, 'All'>;
  source: GlobalSearchSource;
  group: string;
  description?: string;
  meta?: string[];
  keywords: Array<string | number | null | undefined>;
  icon: React.ComponentType<{ className?: string }>;
  status?: string | null;
  date?: string | null;
  tone?: 'green' | 'blue' | 'purple' | 'orange' | 'slate';
};

type RouteSearchItem = Pick<NavLink, 'name' | 'href' | 'viewId'> & {
  category: Exclude<GlobalSearchCategory, 'All'>;
  description: string;
  keywords: string[];
  icon: React.ComponentType<{ className?: string }>;
};

type ScoredResult = GlobalSearchResult & { score?: number };

const SEARCH_STORAGE_KEY = 'kmstack.dashboard.globalSearch.recent';
const MAX_RECENT_SEARCHES = 5;
const MAX_RESULTS = 12;

const quickFilters: GlobalSearchCategory[] = [
  'All',
  'Dashboard',
  'Invoices',
  'Bills',
  'Clients',
  'Suppliers',
  'Employees',
  'Workforce',
  'Reports',
  'Payments',
  'Purchases',
  'Inventory',
  'Automation',
  'Settings',
  'Integrations',
  'Security'
];

const routeSearchItems: RouteSearchItem[] = [
  {
    name: 'Dashboard overview',
    href: '/',
    viewId: DashboardViewTabs.OVERVIEW,
    category: 'Dashboard',
    description: 'Open the business overview, widgets, alerts, and key metrics.',
    keywords: ['dashboard', 'overview', 'home', 'business overview', 'widgets', 'alerts', 'metrics'],
    icon: BarChart3
  },
  {
    name: 'Invoices',
    href: '/org/invoices',
    viewId: DashboardViewTabs.INVOICES,
    category: 'Invoices',
    description: 'Create, track, send, and manage customer invoices.',
    keywords: ['invoice', 'invoices', 'customer invoice', 'sales invoice', 'receivable'],
    icon: ReceiptText
  },
  {
    name: 'Clients',
    href: '/org/clients-suppliers?tab=Clients',
    viewId: 'client',
    category: 'Clients',
    description: 'Open client records and customer contact details.',
    keywords: ['client', 'clients', 'customer', 'customers', 'contact'],
    icon: UsersRound
  },
  {
    name: 'Suppliers',
    href: '/org/clients-suppliers?tab=Suppliers',
    viewId: 'supplier',
    category: 'Suppliers',
    description: 'Open supplier records and vendor contact details.',
    keywords: ['supplier', 'suppliers', 'vendor', 'vendors'],
    icon: Building2
  },
  {
    name: 'Payments',
    href: '/org/payments/tracking',
    viewId: DashboardViewTabs.PAYMENTS,
    category: 'Payments',
    description: 'Track invoice payments and payment status.',
    keywords: ['payment', 'payments', 'paid', 'sent', 'overdue'],
    icon: CreditCard
  },
  {
    name: 'Bills',
    href: '/org/purchases/bills',
    viewId: 'bills',
    category: 'Bills',
    description: 'Review supplier bills and payable activity.',
    keywords: ['bill', 'bills', 'payable', 'supplier bill'],
    icon: FileText
  },
  {
    name: 'Expenses',
    href: '/org/purchases/expenses',
    viewId: 'expenses',
    category: 'Purchases',
    description: 'Review purchase expenses and spend records.',
    keywords: ['purchase', 'purchases', 'expense', 'expenses', 'spend'],
    icon: ShoppingCart
  },
  {
    name: 'Employees',
    href: '/org/workforce/users',
    viewId: 'users',
    category: 'Employees',
    description: 'Open workforce employee records.',
    keywords: ['employee', 'employees', 'team', 'staff', 'workforce'],
    icon: UserRound
  },
  {
    name: 'Time tracking',
    href: '/org/workforce/time-tracking/timer',
    viewId: 'timer',
    category: 'Workforce',
    description: 'Track work time and active timers.',
    keywords: ['time tracking', 'timer', 'track time', 'work hours', 'clock in', 'clock out'],
    icon: Clock3
  },
  {
    name: 'Projects',
    href: '/org/workforce/time-tracking/projects',
    viewId: 'projects',
    category: 'Workforce',
    description: 'Manage work projects used for tracking and billing.',
    keywords: ['projects', 'project tracking', 'job tracking', 'project time'],
    icon: Clock3
  },
  {
    name: 'Timesheets',
    href: '/org/workforce/time-tracking/time-sheets',
    viewId: 'time-sheets',
    category: 'Workforce',
    description: 'Review submitted work timesheets.',
    keywords: ['timesheets', 'time sheets', 'approve hours', 'work logs'],
    icon: FileText
  },
  {
    name: 'Leave approvals',
    href: '/org/workforce/leave-approvals',
    viewId: 'leave-approvals',
    category: 'Workforce',
    description: 'Review leave requests and absence approvals.',
    keywords: ['leave', 'absence', 'vacation', 'holiday', 'leave approval', 'time off'],
    icon: Clock3
  },
  {
    name: 'Payroll processing',
    href: '/org/workforce/payroll-processing',
    viewId: 'payroll-processing',
    category: 'Employees',
    description: 'Run payroll and review payroll processing.',
    keywords: ['payroll', 'salary', 'wages', 'run payroll'],
    icon: Clock3
  },
  {
    name: 'Payslips',
    href: '/org/workforce/payslips',
    viewId: 'payslips',
    category: 'Employees',
    description: 'View and manage employee payslips.',
    keywords: ['payslip', 'payslips', 'pay slips', 'salary slips', 'employee pay'],
    icon: FileText
  },
  {
    name: 'Payroll settings',
    href: '/org/workforce/payroll-settings',
    viewId: 'payroll-settings',
    category: 'Settings',
    description: 'Configure payroll settings.',
    keywords: ['payroll settings', 'salary settings', 'payroll configuration'],
    icon: Settings
  },
  {
    name: 'Purchases reports',
    href: '/org/purchases/reports',
    viewId: 'reports',
    category: 'Reports',
    description: 'Open bill and expense reports.',
    keywords: ['purchase reports', 'expense reports', 'bills reports', 'spend reports'],
    icon: BarChart3
  },
  {
    name: 'Crypto payments',
    href: '/org/payments/crypto',
    viewId: 'crypto',
    category: 'Payments',
    description: 'Open crypto payment tools.',
    keywords: ['crypto', 'crypto payments', 'wallet', 'blockchain payments'],
    icon: CreditCard
  },
  {
    name: 'Inventory',
    href: '/org/inventory/management',
    viewId: 'management',
    category: 'Inventory',
    description: 'Manage products, stock levels, and inventory status.',
    keywords: ['inventory', 'stock', 'product', 'products', 'items', 'service', 'services'],
    icon: Package
  },
  {
    name: 'Purchase orders',
    href: '/org/inventory/purchase-orders',
    viewId: 'purchase-orders',
    category: 'Inventory',
    description: 'Create and manage inventory purchase orders.',
    keywords: ['purchase orders', 'po', 'stock orders', 'supplier orders', 'restock'],
    icon: Package
  },
  {
    name: 'Inventory reports',
    href: '/org/inventory/reports',
    viewId: 'reports',
    category: 'Reports',
    description: 'Open inventory valuation and stock reports.',
    keywords: ['inventory reports', 'stock reports', 'valuation', 'cogs', 'turnover'],
    icon: BarChart3
  },
  {
    name: 'Financial & tax reports',
    href: '/org/report-analytics/reports/financial-tax',
    viewId: EReportCategory.FINANCIAL_TAX,
    category: 'Reports',
    description: 'Open profit and loss, balance sheet, cash flow, ledger, and tax reports.',
    keywords: ['report', 'reports', 'financial', 'tax', 'profit', 'loss', 'balance sheet', 'cash flow', 'ledger'],
    icon: BarChart3
  },
  {
    name: 'Operations reports',
    href: '/org/report-analytics/reports/operations',
    viewId: EReportCategory.OPERATIONS,
    category: 'Reports',
    description: 'Open receivable, payable, payroll, banking, and inventory reports.',
    keywords: ['operations', 'transactions', 'receivable', 'payable', 'payroll report', 'banking report', 'inventory report'],
    icon: BarChart3
  },
  {
    name: 'Analytics & forecasting',
    href: '/org/report-analytics/reports/analytics-forecasting',
    viewId: EReportCategory.ANALYTICS,
    category: 'Reports',
    description: 'Open forecasting and advanced analytics views.',
    keywords: ['analytics', 'forecasting', 'forecast', 'budget', 'management analytics'],
    icon: BarChart3
  },
  {
    name: 'Automation',
    href: '/org/automation',
    viewId: DashboardViewTabs.AUTOMATION,
    category: 'Automation',
    description: 'Open automation tools and workflows.',
    keywords: ['automation', 'automations', 'workflow', 'smart automation'],
    icon: Settings
  },
  {
    name: 'Payment reminders',
    href: '/org/automation/smart-payment-reminders',
    viewId: 'smart-payment-reminders',
    category: 'Automation',
    description: 'Manage smart payment reminders.',
    keywords: ['payment reminders', 'smart reminders', 'late invoice reminders', 'overdue reminders'],
    icon: CreditCard
  },
  {
    name: 'Auto categorization',
    href: '/org/automation/auto-categorization',
    viewId: 'auto-categorization',
    category: 'Automation',
    description: 'Configure AI transaction categorization.',
    keywords: ['auto categorization', 'categorize transactions', 'ai categorization', 'expense automation'],
    icon: Settings
  },
  {
    name: 'Automated tax reports',
    href: '/org/automation/tax-reports',
    viewId: 'tax-reports',
    category: 'Automation',
    description: 'Open tax report automation.',
    keywords: ['tax automation', 'automated tax', 'vat automation', 'tax reports automation'],
    icon: BarChart3
  },
  {
    name: 'Security & compliance',
    href: '/org/security-compliance/dashboard',
    viewId: DashboardViewTabs.SECURITY_COMPLIANCE,
    category: 'Security',
    description: 'Open security, compliance, privacy, and audit views.',
    keywords: ['security', 'compliance', 'privacy', 'audit', 'fraud', 'risk'],
    icon: Settings
  },
  {
    name: 'Data security',
    href: '/org/security-compliance/data-security',
    viewId: 'data-security',
    category: 'Security',
    description: 'Review data security controls.',
    keywords: ['data security', 'privacy controls', 'audit logs', 'access control'],
    icon: Settings
  },
  {
    name: 'Payment security',
    href: '/org/security-compliance/payment-security',
    viewId: 'payment-security',
    category: 'Security',
    description: 'Review payment security and fraud protection.',
    keywords: ['payment security', 'fraud', 'fraud alerts', 'payment risk'],
    icon: CreditCard
  },
  {
    name: 'Compliance privacy',
    href: '/org/security-compliance/compliance-privacy',
    viewId: 'compliance-privacy',
    category: 'Security',
    description: 'Review compliance and privacy settings.',
    keywords: ['compliance privacy', 'gdpr', 'privacy', 'data privacy'],
    icon: Settings
  },
  {
    name: 'Integrations',
    href: '/org/integrations',
    viewId: DashboardViewTabs.INTEGRATIONS,
    category: 'Integrations',
    description: 'Open integration settings and connected services.',
    keywords: ['integrations', 'connected apps', 'marketplace', 'plugins'],
    icon: Settings
  },
  {
    name: 'API integration',
    href: '/org/integrations/api',
    viewId: 'api',
    category: 'Integrations',
    description: 'Manage API keys and API access.',
    keywords: ['api', 'api keys', 'developer api', 'api integration'],
    icon: Settings
  },
  {
    name: 'Webhooks',
    href: '/org/integrations/webhooks',
    viewId: 'webhooks',
    category: 'Integrations',
    description: 'Configure webhook endpoints.',
    keywords: ['webhooks', 'webhook', 'events', 'integration events'],
    icon: Settings
  },
  {
    name: 'Marketplace integrations',
    href: '/org/integrations/marketplace',
    viewId: 'marketplace',
    category: 'Integrations',
    description: 'Browse available app and plugin integrations.',
    keywords: ['marketplace', 'plugins', 'apps', 'connect app', 'integration marketplace'],
    icon: Settings
  },
  {
    name: 'Invoice settings',
    href: '/org/settings/invoice-settings',
    viewId: 'invoice-settings',
    category: 'Settings',
    description: 'Configure invoice templates and defaults.',
    keywords: ['invoice settings', 'invoice templates', 'invoice defaults', 'templates'],
    icon: Settings
  },
  {
    name: 'Payment methods settings',
    href: '/org/settings/payment-methods',
    viewId: 'payment-methods',
    category: 'Settings',
    description: 'Configure payment methods.',
    keywords: ['payment methods', 'payment settings', 'bank payment settings'],
    icon: CreditCard
  },
  {
    name: 'Notification settings',
    href: '/org/settings/notification',
    viewId: 'notification',
    category: 'Settings',
    description: 'Configure notification preferences.',
    keywords: ['notification', 'notifications', 'email notifications', 'alerts settings'],
    icon: Settings
  },
  {
    name: 'Users roles and permissions',
    href: '/org/settings/user-roles-permissions',
    viewId: 'user-roles-permissions',
    category: 'Settings',
    description: 'Manage users, roles, and permissions.',
    keywords: ['users', 'roles', 'permissions', 'user roles', 'access permissions'],
    icon: UsersRound
  },
  {
    name: 'Transaction settings',
    href: '/org/settings/transaction-settings',
    viewId: 'transaction-settings',
    category: 'Settings',
    description: 'Configure transaction and tax rules.',
    keywords: ['transaction settings', 'tax settings', 'chart of accounts settings', 'auto tax'],
    icon: Settings
  },
  {
    name: 'Settings',
    href: '/org/settings',
    viewId: DashboardViewTabs.SETTINGS,
    category: 'Reports',
    description: 'Manage account, invoice, payment, and organization settings.',
    keywords: ['settings', 'preferences', 'configuration', 'account settings'],
    icon: Settings
  }
];

const commandSearchItems: RouteSearchItem[] = [
  {
    name: 'Create invoice',
    href: '/org/invoices?view=create-invoice',
    viewId: DashboardViewTabs.INVOICES,
    category: 'Invoices',
    description: 'Start a new customer invoice.',
    keywords: ['create invoice', 'new invoice', 'add invoice', 'raise invoice', 'make invoice', 'bill customer', 'invoice customer'],
    icon: ReceiptText
  },
  {
    name: 'Record payment',
    href: '/org/payments/tracking',
    viewId: DashboardViewTabs.PAYMENTS,
    category: 'Payments',
    description: 'Open payment tracking to record or review payments.',
    keywords: ['record payment', 'add payment', 'mark paid', 'payment received', 'invoice payment', 'collect payment'],
    icon: CreditCard
  },
  {
    name: 'Create bill',
    href: '/org/purchases/bills?view=create-bill',
    viewId: 'bills',
    category: 'Bills',
    description: 'Create a new supplier bill.',
    keywords: ['create bill', 'new bill', 'add bill', 'supplier bill', 'enter bill', 'record bill'],
    icon: FileText
  },
  {
    name: 'Add client',
    href: '/org/clients-suppliers?tab=Clients&view=add-client',
    viewId: 'client',
    category: 'Clients',
    description: 'Create a new client record.',
    keywords: ['add client', 'create client', 'new client', 'add customer', 'create customer', 'new customer'],
    icon: UsersRound
  },
  {
    name: 'Add supplier',
    href: '/org/clients-suppliers?tab=Suppliers&view=add-supplier',
    viewId: 'supplier',
    category: 'Suppliers',
    description: 'Create a new supplier record.',
    keywords: ['add supplier', 'create supplier', 'new supplier', 'add vendor', 'create vendor', 'new vendor'],
    icon: Building2
  },
  {
    name: 'Add expense',
    href: '/org/purchases/expenses?view=add-transaction',
    viewId: 'expenses',
    category: 'Purchases',
    description: 'Record a new expense transaction.',
    keywords: ['add expense', 'create expense', 'record expense', 'new expense', 'add purchase', 'record purchase', 'add transaction'],
    icon: ShoppingCart
  },
  {
    name: 'Add employee',
    href: '/org/workforce/users?view=add-employee',
    viewId: 'users',
    category: 'Employees',
    description: 'Create a new employee profile.',
    keywords: ['add employee', 'create employee', 'new employee', 'hire employee', 'add staff', 'new staff member'],
    icon: UserRound
  },
  {
    name: 'Run payroll',
    href: '/org/workforce/payroll-processing',
    viewId: 'payroll-processing',
    category: 'Employees',
    description: 'Open payroll processing.',
    keywords: ['run payroll', 'process payroll', 'create payroll', 'pay employees', 'payroll run', 'salary run'],
    icon: Clock3
  },
  {
    name: 'Track time',
    href: '/org/workforce/time-tracking/timer',
    viewId: 'timer',
    category: 'Workforce',
    description: 'Open the time tracker.',
    keywords: ['track time', 'start timer', 'clock in', 'log hours', 'record time', 'timesheet timer'],
    icon: Clock3
  },
  {
    name: 'Review timesheets',
    href: '/org/workforce/time-tracking/time-sheets',
    viewId: 'time-sheets',
    category: 'Workforce',
    description: 'Open submitted timesheets.',
    keywords: ['review timesheets', 'approve timesheets', 'time sheets', 'submitted hours', 'approve hours'],
    icon: FileText
  },
  {
    name: 'Approve leave',
    href: '/org/workforce/leave-approvals',
    viewId: 'leave-approvals',
    category: 'Workforce',
    description: 'Open leave and absence approvals.',
    keywords: ['approve leave', 'review leave', 'vacation request', 'holiday request', 'time off request'],
    icon: Clock3
  },
  {
    name: 'File VAT return',
    href: '/org/report-analytics/reports/financial-tax?view=tax-reports&subview=vat%2Fgst&action=file-vat',
    viewId: EReportCategory.FINANCIAL_TAX,
    category: 'Reports',
    description: 'Open VAT/GST tax reports and filing actions.',
    keywords: ['file vat', 'vat filing', 'file vat return', 'submit vat', 'vat return', 'gst filing', 'tax filing', 'file tax'],
    icon: BarChart3
  },
  {
    name: 'View cash flow',
    href: '/org/report-analytics/reports/financial-tax?view=financial-reports&subview=cash-flow-statement',
    viewId: EReportCategory.FINANCIAL_TAX,
    category: 'Reports',
    description: 'Open the cash flow statement.',
    keywords: ['cash flow', 'cashflow', 'cash flow statement', 'cash report', 'money in money out'],
    icon: BarChart3
  },
  {
    name: 'View balance sheet',
    href: '/org/report-analytics/reports/financial-tax?view=financial-reports&subview=balance-sheet',
    viewId: EReportCategory.FINANCIAL_TAX,
    category: 'Reports',
    description: 'Open the balance sheet.',
    keywords: ['balance sheet', 'assets liabilities', 'statement of financial position'],
    icon: BarChart3
  },
  {
    name: 'Open profit and loss report',
    href: '/org/report-analytics/reports/financial-tax?view=financial-reports&subview=profit-loss-statement',
    viewId: EReportCategory.FINANCIAL_TAX,
    category: 'Reports',
    description: 'Open the profit and loss statement.',
    keywords: ['profit and loss', 'p&l', 'income statement', 'open pnl', 'view profit loss', 'profit report'],
    icon: BarChart3
  },
  {
    name: 'Open general ledger',
    href: '/org/report-analytics/reports/financial-tax?view=general-ledger&subview=general',
    viewId: EReportCategory.FINANCIAL_TAX,
    category: 'Reports',
    description: 'Open the general ledger.',
    keywords: ['general ledger', 'ledger', 'chart of accounts report', 'transaction ledger', 'account balances'],
    icon: BarChart3
  },
  {
    name: 'Add product or service',
    href: '/org/inventory/management?view=add-item',
    viewId: 'management',
    category: 'Inventory',
    description: 'Open inventory management to create an item.',
    keywords: ['add product', 'create product', 'new product', 'add service', 'create service', 'new service', 'add inventory item'],
    icon: Package
  },
  {
    name: 'Create purchase order',
    href: '/org/inventory/purchase-orders',
    viewId: 'purchase-orders',
    category: 'Inventory',
    description: 'Open purchase orders to create or manage stock orders.',
    keywords: ['create purchase order', 'new purchase order', 'add purchase order', 'create po', 'restock inventory', 'order stock'],
    icon: Package
  },
  {
    name: 'Set payment reminder',
    href: '/org/automation/smart-payment-reminders',
    viewId: 'smart-payment-reminders',
    category: 'Automation',
    description: 'Open automated invoice reminder settings.',
    keywords: ['set payment reminder', 'create reminder', 'remind customer', 'overdue reminder', 'invoice reminder'],
    icon: CreditCard
  },
  {
    name: 'Categorize expenses automatically',
    href: '/org/automation/auto-categorization',
    viewId: 'auto-categorization',
    category: 'Automation',
    description: 'Open AI categorization automation.',
    keywords: ['auto categorize', 'categorize expenses', 'categorize transactions', 'ai categorize', 'expense automation'],
    icon: Settings
  },
  {
    name: 'Connect an integration',
    href: '/org/integrations/marketplace',
    viewId: 'marketplace',
    category: 'Integrations',
    description: 'Open integration marketplace.',
    keywords: ['connect integration', 'connect app', 'install plugin', 'add integration', 'marketplace'],
    icon: Settings
  },
  {
    name: 'Configure webhooks',
    href: '/org/integrations/webhooks',
    viewId: 'webhooks',
    category: 'Integrations',
    description: 'Open webhook configuration.',
    keywords: ['configure webhooks', 'setup webhook', 'create webhook', 'webhook events', 'api webhook'],
    icon: Settings
  },
  {
    name: 'Manage roles and permissions',
    href: '/org/settings/user-roles-permissions',
    viewId: 'user-roles-permissions',
    category: 'Settings',
    description: 'Open user role and permission settings.',
    keywords: ['manage roles', 'manage permissions', 'user roles', 'invite user', 'access control', 'team permissions'],
    icon: UsersRound
  },
  {
    name: 'Edit invoice templates',
    href: '/org/settings/invoice-settings',
    viewId: 'invoice-settings',
    category: 'Settings',
    description: 'Open invoice template settings.',
    keywords: ['invoice template', 'edit invoice template', 'invoice branding', 'invoice settings', 'invoice defaults'],
    icon: Settings
  },
  {
    name: 'Review security',
    href: '/org/security-compliance/dashboard',
    viewId: DashboardViewTabs.SECURITY_COMPLIANCE,
    category: 'Security',
    description: 'Open the security and compliance dashboard.',
    keywords: ['review security', 'security dashboard', 'fraud alerts', 'audit logs', 'compliance dashboard'],
    icon: Settings
  }
];

const resultToneClasses = {
  green: 'bg-lightGreen text-primaryGreen',
  blue: 'bg-blue-50 text-blue-600',
  purple: 'bg-purple-50 text-purple-600',
  orange: 'bg-amber-50 text-amber-600',
  slate: 'bg-slate-100 text-slate-600'
};

const readRecentSearches = (): string[] => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.localStorage.getItem(SEARCH_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string').slice(0, MAX_RECENT_SEARCHES) : [];
  } catch {
    return [];
  }
};

const saveRecentSearches = (searches: string[]) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(searches.slice(0, MAX_RECENT_SEARCHES)));
};

const normalizeSearch = (value: string | number | null | undefined) => String(value ?? '').trim().toLowerCase();

const normalizeListResponse = <T,>(response: unknown): T[] => {
  if (Array.isArray(response)) return response as T[];
  if (response && typeof response === 'object') {
    const listResponse = response as { items?: unknown; data?: unknown };
    if (Array.isArray(listResponse.items)) return listResponse.items as T[];
    if (Array.isArray(listResponse.data)) return listResponse.data as T[];
    if (listResponse.data && typeof listResponse.data === 'object' && Array.isArray((listResponse.data as { items?: unknown }).items)) {
      return (listResponse.data as { items: T[] }).items;
    }
  }
  return [];
};

const appendSearchParam = (href: string, searchValue: string) => {
  const separator = href.includes('?') ? '&' : '?';
  return `${ href }${ separator }search=${ encodeURIComponent(searchValue) }`;
};

const formatCurrencyAmount = (amount: number | null | undefined, currency: string) => {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  return formatMoney(amount, currency);
};

const compact = (values: Array<string | number | null | undefined>) => values.filter((value): value is string | number => value !== null && value !== undefined && String(value).trim() !== '').map(String);

const featureFlagsForSearchItem = (item: Pick<NavLink, 'href' | 'name' | 'viewId'>) => {
  const [hrefWithoutQuery] = item.href.split('?');
  return featureFlagsForLink({ ...item, href: hrefWithoutQuery });
};

const scoreResult = (result: GlobalSearchResult, query: string) => {
  const normalizedQuery = normalizeSearch(query);
  const searchableParts = compact([
    result.title,
    result.description,
    result.category,
    result.group,
    result.status,
    result.date,
    ...(result.meta ?? []),
    ...result.keywords
  ]);
  const title = normalizeSearch(result.title);
  const haystack = normalizeSearch(searchableParts.join(' '));

  if (!normalizedQuery) {
    if (result.source === 'command') return 65;
    return result.source === 'page' ? 50 : 0;
  }

  const terms = normalizedQuery.split(/\s+/).filter(Boolean);
  let score = 0;

  if (title === normalizedQuery) score += 1000;
  if (title.startsWith(normalizedQuery)) score += 800;
  if (title.includes(normalizedQuery)) score += 620;
  if (haystack.includes(normalizedQuery)) score += 300;

  const matchedTerms = terms.filter((term) => haystack.includes(term));
  if (matchedTerms.length === terms.length) score += 180;
  score += matchedTerms.length * 35;

  if (result.keywords.some((keyword) => normalizeSearch(keyword) === normalizedQuery)) score += 260;
  if (result.source === 'command') score += 220;
  if (result.source === 'record') score += 90;

  return score;
};

export const DashboardGlobalSearch: React.FC = () => {
  const { sessionContext } = useSessionContext();
  const { code: currencyCode } = useActiveCurrency();
  const api = useApi();
  const dashboardApi = api as Partial<AccountAdminApi> | null;
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchFilter, setActiveSearchFilter] = useState<GlobalSearchCategory>('All');
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => readRecentSearches());
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const userPlan = sessionContext?.activeMembership?.planType ?? PlanType.FREE;
  const userRole = sessionContext?.activeMembership?.role?.name;
  const organizationId = sessionContext?.activeOrganization?.id;
  const shouldLoadRecords = Boolean(isSearchOpen && organizationId);
  const availableRouteItems = useMemo(() => {
    return routeSearchItems.filter((item) => {
      const features = featureFlagsForSearchItem(item);
      return features.length === 0 || hasAnyFeature(userPlan, features, userRole);
    });
  }, [userPlan, userRole]);
  const availableCommandItems = useMemo(() => {
    return commandSearchItems.filter((item) => {
      const features = featureFlagsForSearchItem(item);
      return features.length === 0 || hasAnyFeature(userPlan, features, userRole);
    });
  }, [userPlan, userRole]);
  const availableViewIds = useMemo(() => new Set([...availableRouteItems, ...availableCommandItems].map((item) => item.viewId)), [availableCommandItems, availableRouteItems]);

  const expensesQuery = useKmstackQuery(
    ['dashboard-global-search', 'expenses', organizationId],
    async () => normalizeListResponse<ExpenseTransaction>(await dashboardApi?.expenses?.list<ExpenseTransaction[]>()),
    {
      enabled: Boolean(shouldLoadRecords && dashboardApi?.expenses && availableViewIds.has('expenses')),
      staleTime: 5 * 60 * 1000,
    }
  );

  const employeesQuery = useKmstackQuery(
    ['dashboard-global-search', 'employees', organizationId],
    async () => {
      const response = await dashboardApi?.listEmployees?.();
      return normalizeListResponse<Employee>(response?.data || response);
    },
    {
      enabled: Boolean(shouldLoadRecords && dashboardApi?.listEmployees && availableViewIds.has('users')),
      staleTime: 2 * 60 * 1000,
    }
  );

  const invoicesQuery = useKmstackQuery(
    ['dashboard-global-search', 'invoices', organizationId],
    async () => normalizeListResponse<Invoice>(await dashboardApi?.invoices?.().list<Invoice[]>('?limit=50')),
    {
      enabled: Boolean(shouldLoadRecords && dashboardApi?.invoices && availableViewIds.has(DashboardViewTabs.INVOICES)),
      staleTime: 2 * 60 * 1000,
    }
  );

  const clientsQuery = useKmstackQuery(
    ['dashboard-global-search', 'clients', organizationId],
    async () => normalizeListResponse<ExtendedKmstackABParty>(await dashboardApi?.clientSuppliers?.list<ExtendedKmstackABParty[]>('?type=client')),
    {
      enabled: Boolean(shouldLoadRecords && dashboardApi?.clientSuppliers && availableViewIds.has('client')),
      staleTime: 2 * 60 * 1000,
    }
  );

  const suppliersQuery = useKmstackQuery(
    ['dashboard-global-search', 'suppliers', organizationId],
    async () => normalizeListResponse<ExtendedKmstackABParty>(await dashboardApi?.clientSuppliers?.list<ExtendedKmstackABParty[]>('?type=supplier')),
    {
      enabled: Boolean(shouldLoadRecords && dashboardApi?.clientSuppliers && availableViewIds.has('supplier')),
      staleTime: 2 * 60 * 1000,
    }
  );

  const billsQuery = useKmstackQuery(
    ['dashboard-global-search', 'bills', organizationId],
    async () => normalizeListResponse<Bill>(await dashboardApi?.bills?.list<Bill[]>()),
    {
      enabled: Boolean(shouldLoadRecords && dashboardApi?.bills && availableViewIds.has('bills')),
      staleTime: 2 * 60 * 1000,
    }
  );

  const productServiceQuery = useKmstackQuery(
    ['dashboard-global-search', 'product-service', organizationId],
    async () => normalizeListResponse<Product | Service>(await dashboardApi?.productService?.().list<Array<Product | Service>>()),
    {
      enabled: Boolean(shouldLoadRecords && dashboardApi?.productService && availableViewIds.has('management')),
      staleTime: 2 * 60 * 1000,
    }
  );

  const availableRouteResults = useMemo<GlobalSearchResult[]>(() => {
    return availableRouteItems
      .map((item) => ({
        id: `page-${ item.href }`,
        title: item.name,
        href: item.href,
        category: item.category,
        source: 'page',
        group: 'Pages',
        description: item.description,
        keywords: item.keywords,
        icon: item.icon,
        tone: 'slate'
      }));
  }, [availableRouteItems]);
  const availableCommandResults = useMemo<GlobalSearchResult[]>(() => {
    return availableCommandItems
      .map((item) => ({
        id: `command-${ item.viewId }-${ item.name }`,
        title: item.name,
        href: item.href,
        category: item.category,
        source: 'command',
        group: 'Commands',
        description: item.description,
        keywords: item.keywords,
        icon: item.icon,
        tone: 'green'
      }));
  }, [availableCommandItems]);

  const recordResults = useMemo<GlobalSearchResult[]>(() => {
    const invoiceResults = (invoicesQuery.data ?? []).map((invoice) => {
      const title = invoice.invoiceNumber || invoice.id || 'Invoice';
      const amount = formatCurrencyAmount(invoice.amount, invoice.currency || currencyCode);
      return {
        id: `invoice-${ invoice.id || title }`,
        title,
        href: appendSearchParam('/org/invoices', title),
        category: 'Invoices' as const,
        source: 'record' as const,
        group: 'Invoices',
        description: invoice.client?.name || 'Customer invoice',
        meta: compact([amount, invoice.paymentStatus || invoice.status]),
        keywords: [invoice.client?.name, invoice.invoiceNumber, invoice.id, invoice.status, invoice.paymentStatus, amount],
        icon: ReceiptText,
        status: invoice.paymentStatus || invoice.status,
        date: invoice.invoiceDate || invoice.paymentDue,
        tone: 'green' as const
      };
    });

    const billResults = (billsQuery.data ?? []).map((bill) => {
      const vendorName = bill.vendorName || (typeof bill.vendor === 'string' ? bill.vendor : bill.vendor?.name);
      const title = bill.invoiceNumber || vendorName || bill.id || 'Bill';
      const amount = formatCurrencyAmount(bill.totalAmount ?? bill.remainingAmount, bill.currency || currencyCode);
      return {
        id: `bill-${ bill.id || title }`,
        title,
        href: appendSearchParam('/org/purchases/bills', title),
        category: 'Bills' as const,
        source: 'record' as const,
        group: 'Bills',
        description: vendorName || 'Supplier bill',
        meta: compact([amount, bill.status]),
        keywords: [bill.invoiceNumber, vendorName, bill.status, bill.notes, amount],
        icon: FileText,
        status: bill.status,
        date: bill.dateIssued || bill.dueDate,
        tone: 'blue' as const
      };
    });

    const clientResults = (clientsQuery.data ?? []).map((client) => ({
      id: `client-${ client.id || client.name }`,
      title: client.name || client.legalName || 'Client',
      href: appendSearchParam('/org/clients-suppliers?tab=Clients', client.name || client.legalName || ''),
      category: 'Clients' as const,
      source: 'record' as const,
      group: 'Clients',
      description: client.email || client.legalName || 'Client record',
      meta: compact([client.phone || client.tel, client.city, client.country]),
      keywords: [client.name, client.legalName, client.email, client.phone, client.tel, client.taxId, client.city, client.country],
      icon: UsersRound,
      tone: 'green' as const
    }));

    const supplierResults = (suppliersQuery.data ?? []).map((supplier) => ({
      id: `supplier-${ supplier.id || supplier.name }`,
      title: supplier.name || supplier.legalName || 'Supplier',
      href: appendSearchParam('/org/clients-suppliers?tab=Suppliers', supplier.name || supplier.legalName || ''),
      category: 'Suppliers' as const,
      source: 'record' as const,
      group: 'Suppliers',
      description: supplier.email || supplier.legalName || 'Supplier record',
      meta: compact([supplier.phone || supplier.tel, supplier.city, supplier.country]),
      keywords: [supplier.name, supplier.legalName, supplier.email, supplier.phone, supplier.tel, supplier.taxId, supplier.city, supplier.country],
      icon: Building2,
      tone: 'orange' as const
    }));

    const employeeResults = (employeesQuery.data ?? []).map((employee) => {
      const fullName = `${ employee.firstName || '' } ${ employee.lastName || '' }`.trim() || employee.email || 'Employee';
      return {
        id: `employee-${ employee.id || employee.userId || fullName }`,
        title: fullName,
        href: appendSearchParam('/org/workforce/users', fullName),
        category: 'Employees' as const,
        source: 'record' as const,
        group: 'Employees',
        description: employee.email || 'Employee record',
        meta: compact([employee.department, employee.position, employee.status]),
        keywords: [employee.firstName, employee.lastName, employee.email, employee.department, employee.position, employee.employeeId, employee.status],
        icon: UserRound,
        status: employee.status,
        date: employee.startDate instanceof Date ? employee.startDate.toISOString() : employee.startDate,
        tone: 'purple' as const
      };
    });

    const expenseResults = (expensesQuery.data ?? []).map((expense) => {
      const title = expense.name || expense.description || expense.reference || 'Expense';
      const amount = formatCurrencyAmount(expense.price, expense.currency || currencyCode);
      return {
        id: `expense-${ expense.id || title }`,
        title,
        href: appendSearchParam('/org/purchases/expenses', title),
        category: 'Purchases' as const,
        source: 'record' as const,
        group: 'Expenses',
        description: expense.description || expense.category || 'Expense transaction',
        meta: compact([amount, expense.account, expense.paymentAccountName || expense.paymentAccount]),
        keywords: [expense.name, expense.description, expense.category, expense.account, expense.bank, expense.reference, expense.paymentAccountName, amount],
        icon: ShoppingCart,
        date: expense.date,
        tone: 'orange' as const
      };
    });

    const inventoryResults = (productServiceQuery.data ?? []).map((item) => {
      const title = item.name || item.sku || 'Inventory item';
      const isService = item.productType === 'service';
      const stock = 'availableQty' in item || 'qtyOnHand' in item || 'qty' in item
        ? `${item.availableQty ?? item.qtyOnHand ?? item.qty ?? 0} available`
        : 'Service';
      const status = 'status' in item ? item.status : undefined;
      return {
        id: `inventory-${ item.id || title }`,
        title,
        href: appendSearchParam('/org/inventory/management', title),
        category: 'Inventory' as const,
        source: 'record' as const,
        group: 'Inventory',
        description: item.description || item.category || (isService ? 'Service' : 'Product'),
        meta: compact([item.sku, stock, formatCurrencyAmount(item.price, currencyCode)]),
        keywords: [item.name, item.description, item.sku, item.category, status, item.productType, stock],
        icon: Package,
        status,
        tone: 'blue' as const
      };
    });

    return [
      ...invoiceResults,
      ...billResults,
      ...clientResults,
      ...supplierResults,
      ...employeeResults,
      ...expenseResults,
      ...inventoryResults
    ];
  }, [billsQuery.data, clientsQuery.data, currencyCode, employeesQuery.data, expensesQuery.data, invoicesQuery.data, productServiceQuery.data, suppliersQuery.data]);

  const allResults = useMemo(() => [...availableCommandResults, ...recordResults, ...availableRouteResults], [availableCommandResults, availableRouteResults, recordResults]);

  const searchResults = useMemo<ScoredResult[]>(() => {
    return allResults
      .filter((item) => activeSearchFilter === 'All' || item.category === activeSearchFilter)
      .map((item) => ({ ...item, score: scoreResult(item, searchQuery) }))
      .filter((item) => (item.score ?? 0) > 0)
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || (a.source === 'record' ? -1 : 1))
      .slice(0, MAX_RESULTS);
  }, [activeSearchFilter, allResults, searchQuery]);

  const suggestedResults = useMemo<ScoredResult[]>(() => [...availableCommandResults.slice(0, 4), ...availableRouteResults.slice(0, 4)], [availableCommandResults, availableRouteResults]);
  const visibleResults = searchQuery.trim() ? searchResults : suggestedResults;
  const groupedResults = useMemo(() => {
    return visibleResults.reduce<Array<{ group: string; results: ScoredResult[] }>>((groups, result) => {
      const existingGroup = groups.find((group) => group.group === result.group);
      if (existingGroup) {
        existingGroup.results.push(result);
      } else {
        groups.push({ group: result.group, results: [result] });
      }
      return groups;
    }, []);
  }, [visibleResults]);
  const isLoadingRecords = [expensesQuery, employeesQuery, invoicesQuery, clientsQuery, suppliersQuery, billsQuery, productServiceQuery].some((query) => query.isLoading || query.isFetching);

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
    setActiveResultIndex(0);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setActiveSearchFilter('All');
    setActiveResultIndex(0);
  }, []);

  useEffect(() => {
    setActiveResultIndex(0);
  }, [activeSearchFilter, searchQuery]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!searchContainerRef.current?.contains(event.target as Node)) closeSearch();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch();
      }

      if (event.key === 'Escape') {
        closeSearch();
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeSearch, openSearch]);

  const rememberSearch = (value: string) => {
    const nextValue = value.trim();
    if (!nextValue) return;

    const nextSearches = [nextValue, ...recentSearches.filter((recent) => normalizeSearch(recent) !== normalizeSearch(nextValue))].slice(0, MAX_RECENT_SEARCHES);
    setRecentSearches(nextSearches);
    saveRecentSearches(nextSearches);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    saveRecentSearches([]);
  };

  const openSearchResult = (item: ScoredResult) => {
    rememberSearch(searchQuery || item.title);
    closeSearch();
    navigate(item.href);
  };

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const selectedResult = visibleResults[activeResultIndex] || visibleResults[0];
    if (selectedResult) {
      openSearchResult(selectedResult);
      return;
    }

    rememberSearch(searchQuery);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveResultIndex((index) => Math.min(index + 1, Math.max(visibleResults.length - 1, 0)));
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveResultIndex((index) => Math.max(index - 1, 0));
    }
  };

  let resultIndex = -1;

  return (
    <div ref={searchContainerRef}>
      <button
        className="p-2 text-gray-500 hover:text-primaryGreen hover:bg-gray-100 rounded-full"
        aria-label="Search"
        onClick={openSearch}
      >
        <SearchIcon className="w-5 h-5" />
      </button>

      {isSearchOpen && (
        <div className="fixed left-1/2 top-3 z-50 w-[min(760px,calc(100vw-2rem))] -translate-x-1/2">
          <form
            onSubmit={submitSearch}
            className="flex h-12 items-center gap-3 rounded-xl border border-primaryGreen bg-white px-4 text-sm shadow-lg ring-2 ring-primaryGreen/20"
          >
            <SearchIcon className="h-5 w-5 shrink-0 text-gray-500" />
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Search invoices, customers, suppliers, employees, purchases..."
              className="min-w-0 flex-1 bg-transparent text-sm font-medium text-darkGreen outline-none placeholder:text-gray-400 global-search"
              aria-label="Search across Kmstack"
            />
            {isLoadingRecords ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" /> : null}
            {searchQuery ? (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-darkGreen"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <kbd className="hidden shrink-0 items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-500 sm:inline-flex">
                &#8984; K
              </kbd>
            )}
          </form>

          <div className="mt-2 max-h-[min(760px,calc(100vh-5rem))] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl animate-fade-in-up">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div>
                <p className="text-sm font-bold text-darkGreen">Search Kmstack</p>
                <p className="text-xs font-medium text-gray-500">
                  {searchQuery.trim()
                    ? `${ visibleResults.length } result${ visibleResults.length === 1 ? '' : 's' } across available dashboard data`
                    : 'Jump to a page or search records from your workspace'}
                </p>
              </div>
              {recentSearches.length > 0 ? (
                <button
                  type="button"
                  onClick={clearRecentSearches}
                  className="text-xs font-semibold text-gray-500 hover:text-primaryGreen"
                >
                  Clear recent
                </button>
              ) : null}
            </div>

            <div className="max-h-[calc(min(760px,100vh-5rem)-4rem)] overflow-y-auto">
              <div className="border-b border-gray-100 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-darkGreen">
                  <Clock3 className="h-4 w-4 text-gray-500" />
                  Recent searches
                </div>
                {recentSearches.length > 0 ? (
                  <div className="grid gap-1 sm:grid-cols-2">
                    {recentSearches.map((recent) => (
                      <button
                        key={recent}
                        type="button"
                        onClick={() => {
                          setSearchQuery(recent);
                          setActiveSearchFilter('All');
                          searchInputRef.current?.focus();
                        }}
                        className="flex min-w-0 items-center gap-3 rounded-lg px-2 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <Clock3 className="h-4 w-4 shrink-0 text-gray-400" />
                        <span className="truncate">{recent}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-lg bg-gray-50 px-3 py-3 text-sm font-medium text-gray-500">Recent searches will appear here once you start using global search.</p>
                )}
              </div>

              <div className="border-b border-gray-100 p-4">
                <p className="mb-3 text-sm font-bold text-darkGreen">Quick filters</p>
                <div className="flex flex-wrap gap-2">
                  {quickFilters.map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => {
                        setActiveSearchFilter(filter);
                        searchInputRef.current?.focus();
                      }}
                      className={cn(
                        'rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors',
                        activeSearchFilter === filter
                          ? 'bg-primaryGreen text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-bold text-darkGreen">
                    <FileText className="h-4 w-4 text-gray-500" />
                    {searchQuery.trim() ? 'Top results' : 'Suggested destinations'}
                  </div>
                  {isLoadingRecords ? <span className="text-xs font-semibold text-gray-500">Updating...</span> : null}
                </div>

                {groupedResults.length > 0 ? (
                  <div className="space-y-4">
                    {groupedResults.map((group) => (
                      <div key={group.group}>
                        <div className="mb-1 flex items-center justify-between px-1">
                          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{group.group}</p>
                          <span className="text-[11px] font-semibold text-gray-400">{group.results.length}</span>
                        </div>
                        <div className="space-y-1">
                          {group.results.map((item) => {
                            resultIndex += 1;
                            const currentIndex = resultIndex;
                            const Icon = item.icon;
                            const isActive = currentIndex === activeResultIndex;
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onMouseEnter={() => setActiveResultIndex(currentIndex)}
                                onClick={() => openSearchResult(item)}
                                className={cn(
                                  'flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors',
                                  isActive ? 'bg-lightGreen/70 ring-1 ring-primaryGreen/20' : 'hover:bg-gray-50'
                                )}
                              >
                                <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', resultToneClasses[item.tone ?? 'green'])}>
                                  <Icon className="h-5 w-5" />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="flex min-w-0 items-center gap-2">
                                    <span className="truncate text-sm font-bold text-darkGreen">{item.title}</span>
                                    <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">{item.category}</span>
                                  </span>
                                  <span className="mt-0.5 block truncate text-xs font-medium text-gray-500">
                                    {[item.description, ...(item.meta ?? [])].filter(Boolean).join(' · ')}
                                  </span>
                                </span>
                                <span className="hidden shrink-0 text-right sm:block">
                                  {item.status ? <span className="block text-xs font-bold text-primaryGreen">{item.status}</span> : null}
                                  {item.date ? <span className="block text-xs font-medium text-gray-400">{item.date}</span> : null}
                                </span>
                                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg bg-gray-50 px-4 py-8 text-center">
                    <p className="text-sm font-bold text-darkGreen">No matches found</p>
                    <p className="mt-1 text-sm font-medium text-gray-500">Try a customer, invoice number, employee, supplier, product, or report name.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
